import { isString } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { notification } from 'antd';
import { stepperInfo } from '../components/forms/CreateRide/stepperInfo';
import {
  URLS,
  UBER_HEALTH_ORG_NOT_ELIGIBLE_MESSAGE,
  UBER_INVALID_PHONE_MESSAGE,
  VEHICLE_TYPES,
} from '../constants/constants';
import {
  createDraft,
  deleteDraft,
  operationTypes,
  resetDraft,
  saveDraft,
  setCurrentStep,
  updateDraft,
} from '../store/drafts';
import { LodashHelper } from '../utils';
import { normalizeRideUberPhoneNumbers } from '../utils/phoneNumberHelper';
import { hasQuickScheduleDraftData } from '../utils/buildQuickScheduleDraft';

const COMPACT_NOTIFICATION_STYLE = { width: 'min(480px, 95vw)' };

const isUberAmbulatoryVehicleType = (vehicleType) =>
  vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE;

const crossedUberVehicleTypeBoundary = (previousVehicleType, newVehicleType) => {
  if (!previousVehicleType || !newVehicleType || previousVehicleType === newVehicleType) {
    return false;
  }
  return (
    isUberAmbulatoryVehicleType(previousVehicleType) !==
    isUberAmbulatoryVehicleType(newVehicleType)
  );
};

const clearFlexibleDateTimeFields = (step3) => {
  if (!step3) return step3;

  return {
    ...step3,
    isPickUpDateFlexible: false,
    isReturnDateFlexible: false,
    pickUpDateLowerLimit: undefined,
    returnTimeLowerLimit: undefined,
    pickUpDate: step3.pickUpDate ? { ...step3.pickUpDate, datetime: '' } : step3.pickUpDate,
    returnTime: step3.returnTime ? { ...step3.returnTime, datetime: '' } : step3.returnTime,
    appointmentDate: step3.appointmentDate?.datetime
      ? { ...step3.appointmentDate, datetime: '' }
      : step3.appointmentDate,
  };
};

const getStep3WithClearedFlexibleDatesIfNeeded = (draft, newStep2Values) => {
  const previousVehicleType = draft?.step2?.vehicleType;
  const newVehicleType = newStep2Values?.vehicleType;

  if (!crossedUberVehicleTypeBoundary(previousVehicleType, newVehicleType)) {
    return draft?.step3;
  }

  const step3 = draft?.step3;
  if (!step3?.isPickUpDateFlexible && !step3?.isReturnDateFlexible) {
    return step3;
  }

  return clearFlexibleDateTimeFields(step3);
};

export const useCreateRideHandlers = ({
  step,
  Navigation,
  formikValues,
  draft,
  draftId,
  showThirdStep,
  unblockHistory,
  setModalVisible,
  location,
  QuickScheduleModalVisible,
  draftIsValid,
  isUberEligibleFromAuth,
}) => {
  const dispatch = useDispatch();
  const navigate = Navigation;

  const handleDelete = useCallback(() => {

    if (unblockHistory) {
      unblockHistory.current();
    }
    setModalVisible(false);
    dispatch(deleteDraft({ draftId, pathname: URLS.RIDES, Navigation: navigate }));
  }, [dispatch, draftId, setModalVisible, unblockHistory, navigate]);

  const handleStay = useCallback(() => setModalVisible(false), [setModalVisible]);

  const handlePreviousStep = useCallback(
    (stepNumber) => {
      const rideIsUber = isUberAmbulatoryVehicleType(draft?.step2?.vehicleType);
      let stepNavigateTo = isString(stepNumber) && stepNumber;
      if (step === 0) {
        navigate.push(navigate?.location?.state?.from);
      }

      if (!LodashHelper.isEqual(formikValues, draft?.[`step${step + 1}`])) {
        stepNavigateTo =
          stepNavigateTo ||
          (!showThirdStep && step === 4 && !rideIsUber ? step - 2 : step - 1);
        const clearedStep3 =
          step === 1 ? getStep3WithClearedFlexibleDatesIfNeeded(draft, formikValues) : draft?.step3;

        dispatch(
          updateDraft({
            draft: {
              ...draft,
              ...(clearedStep3 !== draft?.step3 ? { step3: clearedStep3 } : {}),
              [`step${step + 1}`]: formikValues,
            },
            draftId,
            step: stepNavigateTo,
          }),
        );
      }

      if (step === 4 && !showThirdStep && !rideIsUber) {
        dispatch(setCurrentStep(stepNavigateTo || step - 2));
      } else {
        dispatch(setCurrentStep(stepNavigateTo || step - 1));
      }
    },
    [dispatch, draft, draftId, formikValues, navigate, showThirdStep, step],
  );

  const handleSubmit = useCallback(
    (values, stepNumber) => {
      const vehicleTypeOnStep2 =
        values?.vehicleType ??
        values?.step2?.vehicleType ??
        draft?.step2?.vehicleType;
      const rideIsUber = isUberAmbulatoryVehicleType(vehicleTypeOnStep2);
      if (step === 1 && rideIsUber) {
        const uberOk =
          typeof draft?.isUberEnabled === 'boolean'
            ? draft.isUberEnabled
            : Boolean(isUberEligibleFromAuth);
        if (!uberOk) {
          notification.error({
            className: 'create-ride-handler-toast',
            icon: (
              <ExclamationCircleFilled
                style={{ color: 'var(--ant-color-error, #ff4d4f)', fontSize: 22 }}
              />
            ),
            message: UBER_HEALTH_ORG_NOT_ELIGIBLE_MESSAGE,
            duration: 10,
            placement: 'top',
            style: COMPACT_NOTIFICATION_STYLE,
          });
          return;
        }

        const normalizedStepOne = normalizeRideUberPhoneNumbers(draft?.step1);
        if (!normalizedStepOne.isValid) {
          notification.error({
            className: 'create-ride-handler-toast',
            message: UBER_INVALID_PHONE_MESSAGE,
            duration: 6,
            placement: 'top',
            style: COMPACT_NOTIFICATION_STYLE,
          });
          return;
        }
      }

      let stepNavigateTo = isString(stepNumber) && stepNumber;
      if (step === 0 && !draftId && !LodashHelper.isEqual(values, draft?.[`step${step + 1}`])) {
        stepNavigateTo = stepNavigateTo || step + 1;
        return dispatch(
          createDraft({
            draft: { [`step${step + 1}`]: values },
            step: stepNavigateTo,
          }),
        );
      }
      if (!LodashHelper.isEqual(values, draft?.[`step${step + 1}`])) {
        stepNavigateTo =
          stepNavigateTo ||
          (!showThirdStep && step === 2 && !rideIsUber ? step + 2 : step + 1);
        const normalizedStepOne = rideIsUber
          ? normalizeRideUberPhoneNumbers(draft?.step1)
          : null;
        const clearedStep3 =
          step === 1 ? getStep3WithClearedFlexibleDatesIfNeeded(draft, values) : draft?.step3;

        return dispatch(
          updateDraft({
            draft: {
              ...draft,
              ...(normalizedStepOne?.isValid ? { step1: normalizedStepOne.value } : {}),
              ...(clearedStep3 !== draft?.step3 ? { step3: clearedStep3 } : {}),
              [`step${step + 1}`]: values,
            },
            draftId,
            step: stepNavigateTo,
          }),
        );
      }

      if (step === 2 && !showThirdStep && !rideIsUber) {
        stepNavigateTo = stepNavigateTo || step + 2;
        dispatch(setCurrentStep(stepNavigateTo));
      } else {
        stepNavigateTo = stepNavigateTo || step + 1;
        dispatch(setCurrentStep(stepNavigateTo));
      }
    },
    [dispatch, draft, draftId, showThirdStep, step, isUberEligibleFromAuth],
  );

  const closeQuickScheduleDialogue = useCallback((isEditMode) => {
    if (isEditMode) {
      navigate.push(URLS.EDIT_DRAFT, { from: navigate?.location.pathname });
    } else {
      navigate.push(URLS.CREATE_RIDE, { from: navigate?.location.pathname });
    }
    QuickScheduleModalVisible();
  }, [navigate, unblockHistory, navigate, QuickScheduleModalVisible]);


  const swtichToDetailView = useCallback((draft) => {
    if (hasQuickScheduleDraftData(draft)) {
      return dispatch(
        createDraft({
          draft,
          step: 1,
          type: operationTypes.QUICKSCHEDULE_CREATE_DRAFT_AND_CLOSE,
        }, closeQuickScheduleDialogue(true)),
      );
    }

    return closeQuickScheduleDialogue(false);
  }, [dispatch, closeQuickScheduleDialogue]);

  const handleQuickSubmit = useCallback(
    (draftValues) => {
      const rideIsUber = isUberAmbulatoryVehicleType(
        draftValues?.step2?.vehicleType,
      );
      let draftPayload = draftValues;
      if (rideIsUber) {
        const normalizedStepOne = normalizeRideUberPhoneNumbers(draftValues?.step1);
        if (!normalizedStepOne.isValid) {
          notification.error({
            className: 'create-ride-handler-toast',
            message: UBER_INVALID_PHONE_MESSAGE,
            duration: 6,
            placement: 'top',
            style: COMPACT_NOTIFICATION_STYLE,
          });
          return;
        }
        draftPayload = {
          ...draftValues,
          step1: normalizedStepOne.value,
        };
      }

      return dispatch(
        createDraft({
          draft: draftPayload,
          step: 4,
          type: operationTypes.QUICKSCHEDULE_CREATE_DRAFT_AND_CLOSE,
        }, closeQuickScheduleDialogue(false)),
      );

    }, [dispatch, closeQuickScheduleDialogue]
  );

  const saveAndLeaveCallback = useCallback(() => {
    if (unblockHistory) {
      unblockHistory.current();
      if (location) {
        navigate.push(location.location.pathname);
      }
      navigate.push(URLS.DRAFTS);
    }
    dispatch(resetDraft());
  }, [dispatch, location, unblockHistory, navigate]);




  const handleSaveAndClose = useCallback(() => {
    if (step === 0 && !draftId && !LodashHelper.isEqual(formikValues, stepperInfo[step].values)) {
      dispatch(
        createDraft(
          {
            draft: { [`step${step + 1}`]: formikValues },
            type: operationTypes.CREATE_AND_LEAVE,
          },
          saveAndLeaveCallback,
        ),
      );
    } else if (!LodashHelper.isEqual(formikValues, stepperInfo[step]?.values)) {
      dispatch(
        updateDraft(
          {
            draft: { ...draft, [`step${step + 1}`]: formikValues },
            draftId,
            type: operationTypes.UPDATE_AND_LEAVE,
          },
          saveAndLeaveCallback,
        ),
      );
    } else {
      saveAndLeaveCallback();
    }
  }, [saveAndLeaveCallback, dispatch, draft, draftId, formikValues, step]);

  const handleSave = useCallback(
    () => dispatch(saveDraft({ draftId, unblockHistory, Navigation: navigate })),
    [dispatch, draftId, unblockHistory, navigate],
  );

  const handleBack = useCallback(() => navigate.push(navigate?.location?.state?.from), [navigate]);

  const handleLeave = useCallback(() => {

    if (unblockHistory) {
      unblockHistory.current();
      navigate.push(location?.location?.pathname);
    }
  }, [navigate, location, unblockHistory]);

  return {
    handleDelete,
    handleStay,
    handlePreviousStep,
    handleSaveAndClose,
    handleSave,
    handleBack,
    handleLeave,
    handleSubmit,
    handleQuickSubmit,
    swtichToDetailView,
  };
};
